import type { NextRequest } from "@/modules/fetch/types"
import type { AlgoliaJobAdsRequest } from "@/modules/jobAds/client/utils/computeAlgoliaJobAdsRequest"
import type { JobAdDTO } from "@/modules/jobAds/types/jobAdFromAlgolia"
import type { SearchEngineOptions } from "@/modules/search/searchEngine"
import type { MultipleQueriesQuery } from "@algolia/autocomplete-preset-algolia"
import type { MultipleQueriesResponse } from "@algolia/client-search"

import { logServerPromise } from "@/modules/monitoring/logServerPromise"
import { getSearchEngine } from "@/modules/search/searchEngine"

type GetJobAdsFromAlgoliaParams<T> = {
  algoliaJobAdsRequest: AlgoliaJobAdsRequest
  computeResultsMethod: (response: MultipleQueriesResponse<JobAdDTO>) => T
  searchEngineOptions?: SearchEngineOptions
  nextRequest?: NextRequest
}

export const getJobAdsFromAlgolia = async <T>({
  algoliaJobAdsRequest,
  computeResultsMethod,
  searchEngineOptions,
  nextRequest,
}: GetJobAdsFromAlgoliaParams<T>): Promise<T> => {
  const searchQueries = [
    algoliaJobAdsRequest.sponsoredQuery,
    algoliaJobAdsRequest.mainQuery,
    algoliaJobAdsRequest.facetQueries,
    algoliaJobAdsRequest.savedSearchQuery,
  ]
    .flat()
    .filter(Boolean) as MultipleQueriesQuery[]

  const searchEngine = await getSearchEngine(searchEngineOptions)
  const inlinedSearchQueries = JSON.stringify(searchQueries)

  return logServerPromise("getJobAdsFromAlgolia", searchEngine.search<JobAdDTO>(searchQueries), {
    data: {
      algolia_hosts: searchEngine.transporter.hosts.map(({ protocol, url }) => `${protocol}://${url}`).join(","),
      is_default:
        !searchEngineOptions?.isDirectAlgoliaConnectionEnabled &&
        !searchEngineOptions?.isMultiHostConnectionEnabled &&
        !searchEngineOptions?.isSearchProxyV3ConnectionEnabled,
      is_direct: !!searchEngineOptions?.isDirectAlgoliaConnectionEnabled,
      is_multi_hosts: !!searchEngineOptions?.isMultiHostConnectionEnabled,
      is_v3: !!searchEngineOptions?.isSearchProxyV3ConnectionEnabled,
      request_payload_size: inlinedSearchQueries.length,
    },
    nextRequest,
  })
    .then(computeResultsMethod)
    .catch((error: Error) => {
      const isReverseProxyEnabled =
        process.env.NEXT_PUBLIC_ALGOLIA_USE_REVERSE_PROXY === "true" &&
        !searchEngineOptions?.isDirectAlgoliaConnectionEnabled
      throw new Error(
        `A ${
          error.name
        } occurred when fetching job ads from Algolia (using reverse proxy : ${isReverseProxyEnabled}, appId : ${
          searchEngine.appId
        }, userAgent : ${JSON.stringify(searchEngine.transporter.userAgent)}, hosts : ${JSON.stringify(
          searchEngine.transporter.hosts
        )}, headers : ${JSON.stringify(searchEngine.transporter.headers)}): ${error.message}`
      )
    })
}
